import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import React from "react";
// Helper function to format date from ISO to MM/dd/yyyy
// Moved outside component to prevent recreation on every render
const formatDateToMMDDYYYY = (value) => {
    if (!value)
        return undefined;
    // If already in MM/dd/yyyy format, return as is
    const mmddyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (mmddyyyyPattern.test(value)) {
        return value;
    }
    try {
        const date = new Date(value);
        // Check if date is valid
        if (isNaN(date.getTime()))
            return undefined;
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }
    catch (error) {
        console.error("Error formatting date:", error);
        return undefined;
    }
};
const CustomDatePickerPopover = ({ initialEndDate = "", initialStartDate = "", onApply, onCancel, show, timezoneOffset = "UTC+00:00" }) => {
    const [startDate, setStartDate] = React.useState(formatDateToMMDDYYYY(initialStartDate) || undefined);
    const [endDate, setEndDate] = React.useState(formatDateToMMDDYYYY(initialEndDate) || undefined);
    // Only update local state when popover becomes visible (show changes to true)
    // This prevents infinite loops when parent updates the dates after apply
    const prevShowRef = React.useRef(show);
    React.useEffect(() => {
        if (show && !prevShowRef.current) {
            // Popover just opened, sync with initial values
            const formattedStartDate = formatDateToMMDDYYYY(initialStartDate);
            const formattedEndDate = formatDateToMMDDYYYY(initialEndDate);
            setStartDate(formattedStartDate || undefined);
            setEndDate(formattedEndDate || undefined);
        }
        prevShowRef.current = show;
    }, [show, initialStartDate, initialEndDate]);
    const handleApply = () => {
        // Only call onApply if start date is selected
        if (startDate) {
            // If end date is not selected, use today's date in project timezone
            let finalEndDate = endDate;
            if (!finalEndDate) {
                // Calculate today in the project's timezone
                const getCurrentDateInTimezone = () => {
                    const now = new Date();
                    // Extract offset hours from format like "UTC+01:00" or "+09:00"
                    const offsetStr = timezoneOffset.replace("UTC", "");
                    const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
                    if (offsetMatch) {
                        const sign = offsetMatch[1] === "+" ? 1 : -1;
                        const hours = parseInt(offsetMatch[2], 10);
                        const minutes = parseInt(offsetMatch[3] || "0", 10);
                        const totalOffsetMinutes = sign * (hours * 60 + minutes);
                        // Get UTC time and apply project timezone offset
                        const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
                        const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
                        return projectTime;
                    }
                    return now;
                };
                const today = getCurrentDateInTimezone();
                // Use local date components to avoid UTC conversion
                const month = String(today.getMonth() + 1).padStart(2, "0");
                const day = String(today.getDate()).padStart(2, "0");
                const year = today.getFullYear();
                // Format as MM/dd/yyyy to match the date picker format
                finalEndDate = `${month}/${day}/${year}`;
            }
            onApply(startDate, finalEndDate);
        }
    };
    const handleCancel = () => {
        // Reset to initial values on cancel (convert to MM/dd/yyyy format)
        setStartDate(formatDateToMMDDYYYY(initialStartDate) || undefined);
        setEndDate(formatDateToMMDDYYYY(initialEndDate) || undefined);
        onCancel();
    };
    const handleStartDateChange = (e) => {
        const formattedDate = formatDateToMMDDYYYY(e.detail?.value);
        setStartDate(formattedDate);
    };
    const handleEndDateChange = (e) => {
        const formattedDate = formatDateToMMDDYYYY(e.detail?.value);
        setEndDate(formattedDate);
    };
    if (!show) {
        return null;
    }
    return (_jsx("div", { className: "mb-4 flex justify-end relative", children: _jsxs("div", { className: "absolute top-0 right-0 z-50 border rounded-lg bg-white shadow-lg w-[450px]", "data-testid": "custom-date-picker-popover", children: [_jsx("div", { className: "p-4", children: _jsxs("div", { className: "flex gap-4", children: [_jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["Start Date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx(CatDatepickerField, { ariaDescribedBy: "custom-start-date-picker", className: "w-full", dateFormat: "MM/dd/yyyy", fieldId: "custom-start-date-picker", onBlDatePickerChange: handleStartDateChange, placeholder: "mm/dd/yyyy", size: "sm", value: startDate || "" })] }), _jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["End Date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx(CatDatepickerField, { ariaDescribedBy: "custom-end-date-picker", className: "w-full", dateFormat: "MM/dd/yyyy", fieldId: "custom-end-date-picker", onBlDatePickerChange: handleEndDateChange, placeholder: "mm/dd/yyyy", size: "sm", value: endDate || "" })] })] }) }), _jsxs("div", { className: "border-t px-4 py-3 flex justify-start gap-2", children: [_jsx(CatButton, { className: "bg-yellow-400 text-black font-medium", "data-testid": "apply-button", onClick: handleApply, variant: "primary", children: "Apply" }), _jsx(CatButton, { "data-testid": "cancel-button", onClick: handleCancel, variant: "ghost", children: "Cancel" })] })] }) }));
};
export default CustomDatePickerPopover;
